import type { DetectedFace } from "./auto-reframe-engine";
import { clamp01, toPixelSize } from "./reframe-crop-geometry";

/**
 * Base URL for the self-hosted MediaPipe assets. Nothing here reaches a
 * third-party CDN, so face and object detection stay switched off until these
 * files are published under this prefix:
 *
 *   <base>/wasm/                  the whole @mediapipe/tasks-vision wasm folder
 *   <base>/blaze_face_short_range.tflite
 *   <base>/efficientdet_lite0.tflite
 *
 * The wasm folder ships inside node_modules/@mediapipe/tasks-vision/wasm and
 * must be re-published from the exact version this package depends on: the
 * loader and its binary are versioned together. The resolver picks between the
 * SIMD and nosimd builds at runtime, so upload the directory whole rather than
 * cherry-picking a pair. The two .tflite files are the BlazeFace short-range
 * and EfficientDet-Lite0 task bundles.
 */
export const DEFAULT_VISION_ASSET_BASE_URL = "/models/mediapipe";

export const FACE_MODEL_FILENAME = "blaze_face_short_range.tflite";
export const OBJECT_MODEL_FILENAME = "efficientdet_lite0.tflite";

export interface VisionAssetUrls {
  wasmBase: string;
  faceModel: string;
  objectModel: string;
}

/** Where a frame's subjects came from, in fallback order. */
/**
 * Where a frame's subject came from, in descending order of trust.
 *
 * "centre-fallback" is not a detection. It is the invented box the skin-tone
 * stage returns when it finds nothing, and it must stay distinguishable from
 * "skin-tone" because reporting them together let the UI describe a fabricated
 * subject as a heuristic that had found one.
 */
export type SubjectDetectionSource =
  | "face"
  | "object"
  | "skin-tone"
  | "centre-fallback"
  | "none";

export interface SubjectDetectionResult {
  subjects: DetectedFace[];
  source: SubjectDetectionSource;
}

export interface SubjectDetectionStage {
  source: SubjectDetectionSource;
  run: () => Promise<DetectedFace[]> | DetectedFace[];
}

/** The subset of MediaPipe's `Detection` that reframing reads. */
export interface MediaPipeDetectionLike {
  boundingBox?: {
    originX: number;
    originY: number;
    width: number;
    height: number;
  };
  categories?: ReadonlyArray<{ score: number; categoryName?: string }>;
}

export interface DetectionConversionOptions {
  minConfidence?: number;
  /** Category names to keep. Every category is kept when omitted. */
  allowedCategories?: readonly string[];
}

export interface SubjectSelectionOptions {
  /** 0 picks the largest subject, 1 picks the most central one. */
  centerWeight?: number;
}

const DEFAULT_CENTER_WEIGHT = 0.4;

export function joinAssetPath(baseUrl: string, path: string): string {
  const base = baseUrl.replace(/\/+$/, "");
  const suffix = path.replace(/^\/+/, "");
  return base.length > 0 ? `${base}/${suffix}` : `/${suffix}`;
}

export function resolveVisionAssetUrls(
  baseUrl: string = DEFAULT_VISION_ASSET_BASE_URL,
): VisionAssetUrls {
  return {
    wasmBase: joinAssetPath(baseUrl, "wasm"),
    faceModel: joinAssetPath(baseUrl, FACE_MODEL_FILENAME),
    objectModel: joinAssetPath(baseUrl, OBJECT_MODEL_FILENAME),
  };
}

export function detectionsToSubjects(
  detections: readonly MediaPipeDetectionLike[] | undefined,
  frameWidth: number,
  frameHeight: number,
  options: DetectionConversionOptions = {},
): DetectedFace[] {
  if (!detections?.length) return [];

  const width = toPixelSize(frameWidth);
  const height = toPixelSize(frameHeight);
  const minConfidence = options.minConfidence ?? 0;
  const allowedCategories = options.allowedCategories;
  const subjects: DetectedFace[] = [];

  for (const detection of detections) {
    const box = detection.boundingBox;
    if (!box) continue;

    const category = detection.categories?.[0];
    const confidence = clamp01(category?.score ?? 0);
    if (confidence < minConfidence) continue;
    if (
      allowedCategories &&
      !allowedCategories.some((name) => name === category?.categoryName)
    ) {
      continue;
    }

    // MediaPipe boxes routinely extend past the frame edge. Intersect rather
    // than shift, so a half-visible subject is framed on the half that exists.
    const left = Math.max(0, box.originX);
    const top = Math.max(0, box.originY);
    const right = Math.min(width, box.originX + box.width);
    const bottom = Math.min(height, box.originY + box.height);
    if (!(right > left) || !(bottom > top)) continue;

    subjects.push({
      x: left,
      y: top,
      width: right - left,
      height: bottom - top,
      confidence,
    });
  }

  return subjects;
}

export function selectPrimarySubject(
  subjects: readonly DetectedFace[],
  frameWidth: number,
  frameHeight: number,
  options: SubjectSelectionOptions = {},
): DetectedFace | null {
  if (subjects.length === 0) return null;
  if (subjects.length === 1) return subjects[0];

  const width = toPixelSize(frameWidth);
  const height = toPixelSize(frameHeight);
  const frameArea = width * height;
  const frameCenterX = width / 2;
  const frameCenterY = height / 2;
  const maxDistance = Math.hypot(frameCenterX, frameCenterY);
  const centerWeight = clamp01(options.centerWeight ?? DEFAULT_CENTER_WEIGHT);

  let best = subjects[0];
  let bestScore = Number.NEGATIVE_INFINITY;

  for (const subject of subjects) {
    const area = clamp01((subject.width * subject.height) / frameArea);
    // Square-rooted so a subject filling a quarter of the frame scores 0.5
    // rather than 0.25. Raw area is so small for a face that centrality would
    // otherwise decide every comparison on its own.
    const areaScore = Math.sqrt(area);

    const distance = Math.hypot(
      subject.x + subject.width / 2 - frameCenterX,
      subject.y + subject.height / 2 - frameCenterY,
    );
    const centralityScore =
      maxDistance > 0 ? clamp01(1 - distance / maxDistance) : 1;

    const geometryScore =
      (1 - centerWeight) * areaScore + centerWeight * centralityScore;
    // Confidence scales the score into [0.5, 1] of itself instead of
    // multiplying it directly: a detector that reports no score at all must
    // not flatten every candidate to zero and make the choice arbitrary.
    const score = geometryScore * (0.5 + 0.5 * clamp01(subject.confidence));

    if (score > bestScore) {
      bestScore = score;
      best = subject;
    }
  }

  return best;
}

export const DETECTION_SOURCE_LABELS: Record<SubjectDetectionSource, string> = {
  face: "face detection",
  object: "object detection",
  "skin-tone": "a skin-tone heuristic",
  "centre-fallback": "no subject signal, so a centred crop",
  none: "no subject signal",
};

/** The source that carried most frames. Ties go to the first one recorded. */
export function dominantDetectionSource(
  counts: ReadonlyMap<SubjectDetectionSource, number>,
): SubjectDetectionSource {
  let dominant: SubjectDetectionSource = "none";
  let best = 0;

  for (const [source, count] of counts) {
    if (count > best) {
      best = count;
      dominant = source;
    }
  }

  return dominant;
}

export async function runDetectionChain(
  stages: readonly SubjectDetectionStage[],
): Promise<SubjectDetectionResult> {
  for (const stage of stages) {
    let subjects: DetectedFace[];
    try {
      subjects = await stage.run();
    } catch {
      // A stage that throws is treated exactly like one that saw nothing, so a
      // model that fails mid-clip still hands the frame to the next fallback.
      continue;
    }
    if (subjects.length > 0) {
      return { subjects, source: stage.source };
    }
  }

  return { subjects: [], source: "none" };
}
